import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import {
  aroundIdMissingFromLoadedPage,
  aroundIdQueryVariable,
  aroundIdResetKeyChange
} from "@/hooks/aroundIdPagination";

export { aroundIdMissingFromLoadedPage, aroundIdQueryVariable };

const EXPAND_NOTES_PARAM = "expandNotes";

function deepLinkTargetFromSearchParams(searchParams: URLSearchParams) {
  return searchParams.get(EXPAND_NOTES_PARAM) || undefined;
}

interface UseEventRegistrantAroundIdOptions {
  resetKey?: string;
}

export function useEventRegistrantAroundId({ resetKey }: UseEventRegistrantAroundIdOptions = {}) {
  const [searchParams] = useSearchParams();
  const [aroundId, setAroundId] = useState<string | undefined>(() => deepLinkTargetFromSearchParams(searchParams));
  const previousResetKey = useRef(resetKey);
  const previousTarget = useRef(deepLinkTargetFromSearchParams(searchParams));
  const keepAroundIdThroughNextResetRef = useRef(false);

  useEffect(() => {
    const nextAroundId = deepLinkTargetFromSearchParams(searchParams);
    const resetChange = aroundIdResetKeyChange(previousResetKey, resetKey, keepAroundIdThroughNextResetRef);

    if (resetChange === "clear") {
      previousTarget.current = nextAroundId;
      setAroundId(undefined);
      return;
    }

    if (resetChange === "keep") {
      previousTarget.current = nextAroundId;
      return;
    }

    if (nextAroundId && nextAroundId !== previousTarget.current) {
      setAroundId(nextAroundId);
    }
    previousTarget.current = nextAroundId;
  }, [resetKey, searchParams]);

  const clearAroundId = useCallback(() => {
    keepAroundIdThroughNextResetRef.current = false;
    setAroundId(undefined);
  }, []);

  const keepAroundIdThroughNextReset = useCallback((keep = true) => {
    keepAroundIdThroughNextResetRef.current = keep;
  }, []);

  return { aroundId, clearAroundId, keepAroundIdThroughNextReset };
}

interface UseEventRegistrantListDeepLinkOptions {
  loading: boolean;
  rowIds: string[];
  serverCurrentPage?: number;
  onTargetMissing?: () => void;
  hasBlockingFilters?: boolean;
  aroundId?: string;
}

export function useEventRegistrantListDeepLink({
  loading,
  rowIds,
  serverCurrentPage,
  onTargetMissing,
  hasBlockingFilters = false,
  aroundId
}: UseEventRegistrantListDeepLinkOptions) {
  const [searchParams] = useSearchParams();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [highlightRowId, setHighlightRowId] = useState<string | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const scrolledHighlightIdRef = useRef<string | null>(null);
  const expandNotes = searchParams.get(EXPAND_NOTES_PARAM);

  useEffect(() => {
    if (!expandNotes) {
      return;
    }

    if (loading || serverCurrentPage === undefined) {
      return;
    }

    if (expandNotes && !rowIds.includes(expandNotes) && (hasBlockingFilters || aroundId === expandNotes)) {
      return;
    }

    const next = new URLSearchParams(searchParams);
    if (serverCurrentPage > 0) {
      next.set("currentPage", String(serverCurrentPage));
    } else {
      next.delete("currentPage");
    }
    const nextSearch = next.toString();
    if (nextSearch !== searchParams.toString()) {
      navigate({ pathname, search: nextSearch ? `?${nextSearch}` : "" }, { replace: true });
    }

    if (rowIds.includes(expandNotes)) {
      setHighlightRowId(expandNotes);
    } else {
      setHighlightRowId(null);
      onTargetMissing?.();
    }
  }, [
    aroundId,
    expandNotes,
    hasBlockingFilters,
    loading,
    navigate,
    onTargetMissing,
    pathname,
    rowIds,
    searchParams,
    serverCurrentPage
  ]);

  useEffect(() => {
    if (!highlightRowId) {
      scrolledHighlightIdRef.current = null;
      return;
    }

    if (scrolledHighlightIdRef.current === highlightRowId) {
      return;
    }

    let cancelled = false;
    let attempt = 0;
    let timeoutId: number | undefined;
    let rafId: number | undefined;

    const tryScroll = () => {
      if (cancelled) {
        return;
      }

      const row = containerRef.current?.querySelector("tr.row-highlight") as HTMLElement | null;
      if (row) {
        scrolledHighlightIdRef.current = highlightRowId;
        row.scrollIntoView({ behavior: "smooth", block: "center" });
        return;
      }

      if (attempt++ >= 30) {
        return;
      }

      rafId = requestAnimationFrame(tryScroll);
    };

    timeoutId = window.setTimeout(tryScroll, 0);

    return () => {
      cancelled = true;
      if (timeoutId !== undefined) {
        window.clearTimeout(timeoutId);
      }
      if (rafId !== undefined) {
        cancelAnimationFrame(rafId);
      }
    };
  }, [highlightRowId]);

  return { highlightRowId, containerRef };
}

export function eventRegistrantNoteCopyLink(origin: string, pathname: string, registrationId: string, noteId: string) {
  const params = new URLSearchParams({
    expandNotes: registrationId,
    highlightNote: noteId
  });
  return `${origin}${pathname}?${params.toString()}`;
}
